/**
 * Retrieve one page of projects, with server-side sorting and filtering.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {ProjectListQuery} query - paging, sort and filter options
 * @returns {Promise<Paginated<ProjectInterface>>} the requested page
 * @throws Will throw an error if the request fails.
 */

import { API_URL, ApiError, authenticatedFetch, parseApiErrorMessage } from "../config/api";
import { logger } from "../utils/logger";
import { ItemListQuery, LIST_DEFAULT_SIZE, LIST_MAX_SIZE, Paginated, ProjectListQuery } from "../types/pagination";
import { getCachedItemList, itemListCacheKey, setCachedItemList } from "../utils/itemListCache";
import { appendFlag, appendListFilter, appendPageParams, appendTextFilter } from "./listParams";

export const getProjects = async (_token?: string, query: ProjectListQuery = {}): Promise<ProjectsInterface> => {
  try {
    const params = new URLSearchParams({
      include_sites: 'true',
      t: String(new Date().getTime()),
    });
    appendPageParams(params, query, LIST_DEFAULT_SIZE);
    appendFlag(params, 'archived', query.archived);
    appendTextFilter(params, 'q', query.q);
    appendTextFilter(params, 'name', query.name);
    appendTextFilter(params, 'description', query.description);
    appendTextFilter(params, 'sales_order_number', query.salesOrderNumber);
    appendListFilter(params, 'status_id', query.statusIds);
    appendListFilter(params, 'owner_id', query.ownerIds);
    appendListFilter(params, 'client_id', query.clientIds);
    appendFlag(params, 'owner_is_null', query.ownerIsNull);
    appendFlag(params, 'client_is_null', query.clientIsNull);
    appendTextFilter(params, 'created_at_from', query.createdAtFrom);
    appendTextFilter(params, 'created_at_to', query.createdAtTo);
    appendTextFilter(params, 'start_date_from', query.startDateFrom);
    appendTextFilter(params, 'start_date_to', query.startDateTo);
    appendTextFilter(params, 'end_date_from', query.endDateFrom);
    appendTextFilter(params, 'end_date_to', query.endDateTo);
    appendTextFilter(params, 'risk', query.risk);
    const r = await authenticatedFetch(`${API_URL}/projects/?${params}`);
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    const data:ProjectsInterface = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Fetch every project by walking all pages at the maximum page size.
 * Only for views that genuinely need the full set (map, dashboards, pickers).
 * @param {Omit<ProjectListQuery, 'page' | 'size'>} query - filters and sort applied to every page
 * @returns {Promise<ProjectInterface[]>} all matching projects
 */
export const getAllProjects = async (query: Omit<ProjectListQuery, 'page' | 'size'> = {}): Promise<ProjectsInterface["results"]> => {
  const all: ProjectsInterface["results"] = [];
  for (let page = 1; ; page++) {
    const data = await getProjects(undefined, { ...query, page, size: LIST_MAX_SIZE });
    all.push(...(data.results ?? []));
    if (!data.results?.length || all.length >= data.total) break;
  }
  return all;
};

/**
 * Count, per project, the non-completed task and action items at a risk level.
 * Non-admin users only get counts for projects they are a member of.
 * @param {string} risk - risk level, e.g. HIGH
 * @returns {Promise<Record<string, number>>} project ID to matching item count
 * @throws Will throw an error if the request fails.
 */
export const getProjectRiskCounts = async (risk: string): Promise<Record<string, number>> => {
  const params = new URLSearchParams({ risk });
  const r = await authenticatedFetch(`${API_URL}/projects/risk-counts?${params}`);
  if (!r.ok) {
    throw new Error(`HTTP ${r.status}: ${r.statusText}`);
  }
  const data: { counts: Record<string, number> } = await r.json();
  return data.counts;
};

/**
 * Get a project by its ID with automatic token refresh.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} id - the ID of the project to get
 * @param {any} _keycloak - deprecated, not used
 * @param {Object} options - optional parameters
 * @param {boolean} options.archived - if true, fetch an archived project
 * @returns {Promise<ProjectInterface>} the project
 * @throws if there is an error (error will have a status field if HTTP error)
 */
export const getProjectById = async (_token: string, id: string, _keycloak?: unknown, options?: { archived?: boolean }): Promise<ProjectInterface> => {
  try {
    const params = new URLSearchParams({ include_sites: 'true' });
    if (options?.archived) params.set('archived', 'true');
    const r = await authenticatedFetch(`${API_URL}/projects/${id}?${params}`);

    if (!r.ok) {
      throw new ApiError(r.status, r.statusText);
    }

    const data:ProjectInterface = await r.json();
    return data;
  } catch(e) {
    if (e instanceof ApiError) throw e;
    console.error('❌ getProjectById error:', e);
    throw new ApiError(500, 'Internal Server Error', undefined, e instanceof Error ? e.message : 'Failed to load project');
  }
};

/**
 * Create a project.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {ProjectInterface} data - the data to create the project with
 * @returns {Promise<ProjectInterface>} the created project
 * @throws if there is an error
 */
export const createProject = async (_token: string, data: ProjectInterface): Promise<ProjectInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/`,{
      method: 'POST',
      body: JSON.stringify(data)
    })
    const res:ProjectInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

  /**
   * Update a project by its ID.
   * @param {string} _token - deprecated, token is handled automatically
   * @param {string} id - the ID of the project to update
   * @param {ProjectInterface} data - the data to update the project with
   * @returns {Promise<ProjectInterface>} the updated project
   * @throws if there is an error
   */
export const updateProject = async (_token: string, id: string, data: ProjectInterface): Promise<any> => {
  try {
    const res = await authenticatedFetch(`${API_URL}/projects/${id}`,{
      method: 'PUT',
      body: JSON.stringify(data)
    })

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

  /**
   * Delete a project by its ID.
   * @param {string} _token - deprecated, token is handled automatically
   * @param {string} id - the ID of the project to delete
   * @returns {Promise<ProjectInterface>} the deleted project
   * @throws if there is an error
   */
export const deleteProject = async (_token: string, id: string): Promise<ProjectInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}`,{
      method: 'DELETE'
    })
    const res:ProjectInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Restore an archived project to active state.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} id - the ID of the project to restore
 * @returns {Promise<ProjectInterface>} the restored project
 * @throws if there is an error
 */
export const restoreProject = async (_token: string, id: string): Promise<ProjectInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/unarchive`, {
      method: 'POST'
    });
    if (!r.ok) {
      throw new ApiError(r.status, r.statusText);
    }
    const res: ProjectInterface = await r.json();
    return res;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(500, 'Internal Server Error', undefined, e instanceof Error ? e.message : 'Failed to restore project');
  }
};

export interface CloneProjectRequest {
  name: string;
  clone_sites?: boolean;
  clone_assets?: boolean;
  clone_project_items?: boolean;
  clone_action_items?: boolean;
  clone_daily_updates?: boolean;
  clone_meeting_notes?: boolean;
  clone_documents?: boolean;
  clone_assignments?: boolean;
}

export interface CloneProjectResponse {
  source_project_id: string;
  new_project_id: string;
  name: string;
  is_active: boolean;
  cloned: Record<string, number>;
}

/**
 * Clone a project. The backend copies the selected entity types; defaults
 * mirror the backend schema (sites/assets/items on, everything else off).
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} id - the ID of the project to clone
 * @param {CloneProjectRequest} data - name plus which entity types to clone
 * @returns {Promise<CloneProjectResponse>} the clone response with new_project_id
 * @throws if there is an error
 */
export const cloneProject = async (
  _token: string,
  id: string,
  data: CloneProjectRequest
): Promise<CloneProjectResponse> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/clone`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!r.ok) {
      throw new ApiError(r.status, r.statusText);
    }
    const res: CloneProjectResponse = await r.json();
    return res;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(500, 'Internal Server Error', undefined, e instanceof Error ? e.message : 'Failed to clone project');
  }
};

  /**
   * Retrieve all phases of a project.
   * @param {string} _token - deprecated, token is handled automatically
   * @param {string} id - The ID of the project to get the phases from.
   * @returns {Promise<ProjectPhaseInterface[]>} A promise that resolves to the list of phases.
   * @throws Will throw an error if the request fails.
   */
export const getProjectPhases = async (_token: string, id: string): Promise<ProjectPhaseInterface[]> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/phases?size=1000`);
    const data:ProjectPhaseInterface[] = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve a specific phase of a project by its ID and phase ID.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project.
 * @param {string} phaseId - The ID of the phase to retrieve.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the project phase.
 * @throws Will throw an error if the request fails.
 */

export const getProjectPhaseById = async (_token: string, id: string, phaseId: string): Promise<ProjectPhaseInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/phases/${phaseId}`);
    const data:ProjectPhaseInterface = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create a phase for a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to create the phase for.
 * @param {ProjectPhaseInterface} data - The data to create the phase with.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the created phase.
 * @throws Will throw an error if the request fails.
 */
export const createProjectPhase = async (_token: string, id: string, data: ProjectPhaseInterface): Promise<any> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/phases`,{
      method: 'POST',
      body: JSON.stringify({
        ...data,
      })
    })
    const res:ProjectPhaseInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update a specific phase of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to update the phase for.
 * @param {string} phaseId - The ID of the phase to update.
 * @param {ProjectPhaseInterface} data - The data to update the phase with.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the updated phase.
 * @throws Will throw an error if the request fails.
 */
export const updateProjectPhase = async (_token: string, id: string, phaseId: string, data: ProjectPhaseInterface): Promise<ProjectPhaseInterface> => {
  try {
    // Remove only the phase id, keep project_id as it's required by API
    const { id: _id, ...updateData } = data;
    // Ensure project_id is included
    const requestData = {
      ...updateData,
      project_id: id
    };
    
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/phases/${phaseId}`,{
      method: 'PUT',
      body: JSON.stringify(requestData)
    })
    
    if (!r.ok) {
      const errorText = await r.text();
      throw new Error(`Failed to update phase: ${r.status} ${r.statusText} - ${errorText}`);
    }
    
    // API may return 204 No Content (empty response) on successful update
    const contentType = r.headers.get('content-type');
    if (r.status === 204 || !contentType || !contentType.includes('application/json')) {
      // Return the updated data since API doesn't return anything
      return { ...data, id: phaseId, project_id: id } as ProjectPhaseInterface;
    }
    
    const res:ProjectPhaseInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Delete a specific phase of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to delete the phase from.
 * @param {string} phaseId - The ID of the phase to delete.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the deleted phase.
 * @throws Will throw an error if the request fails.
 */
export const deleteProjectPhase = async (_token: string, id: string, phaseId: string): Promise<ProjectPhaseInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/phases/${phaseId}`,{
      method: 'DELETE'
    })
    const res:ProjectPhaseInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve all users of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to retrieve the users of.
 * @returns {Promise<ProjectUsersResponse>} A promise that resolves to the list of users with roles.
 * @throws Will throw an error if the request fails.
 */
export const getProjectUsers = async (_token: string, id: string, options?: { archived?: boolean; page?: number; size?: number }): Promise<ProjectUsersResponse> => {
  try {
    const url = new URL(`${API_URL}/projects/${id}/users`);
    const size = options?.size ?? 1000;
    const page = options?.page ?? 1;
    url.searchParams.set('size', String(size));
    url.searchParams.set('page', String(page));
    if (options?.archived) {
      url.searchParams.set('archived', 'true');
    }
    const r = await authenticatedFetch(url.toString());
    const data: ProjectUsersResponse = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get project user roles.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to get roles for.
 * @returns {Promise<any[]>} A promise that resolves to the list of available roles.
 * @throws Will throw an error if the request fails.
 */
export const getProjectUserRoles = async (_token: string, id: string): Promise<any[]> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/users/roles`);
    const data: any[] = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Add a user to a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to add the user to.
 * @param {string} userId - The ID of the user to add to the project.
 * @param {any} userData - The data to add the user with.
 * @returns {Promise<ProjectUserWithRole>} A promise that resolves to the added user.
 * @throws Will throw an error if the request fails.
 */
export const addUserToProject = async (_token: string, id: string, _userId: string, userData: any): Promise<ProjectUserWithRole> => {
  try {
    // For creation, API requires: project_id, first_name, last_name, email
    const requestData: any = {
      project_id: id,
      first_name: userData.first_name,
      last_name: userData.last_name,
      email: userData.email
    };

    // Add optional fields only if they exist
    if (userData.phone) {
      requestData.phone = userData.phone;
    }
    
    if (userData.project_role_id) {
      requestData.project_role_id = userData.project_role_id;
    }
    
    if (userData.escalation_level_id) {
      requestData.escalation_level_id = userData.escalation_level_id;
    }
    
    if (userData.department) {
      requestData.department = userData.department;
    }
    
    if (userData.position) {
      requestData.position = userData.position;
    }
    
    if (userData.status) {
      requestData.status = userData.status;
    }
    
    // Skills can be null or string
    if (userData.skills !== undefined) {
      requestData.skills = userData.skills || null;
    }

    const r = await authenticatedFetch(`${API_URL}/projects/${id}/users`,{
      method: 'POST',
      body: JSON.stringify(requestData)
    });

    if (!r.ok) {
      const errorText = await r.text();
      logger.error('addUserToProject: API error', r.status, errorText);

      let parsedResponse: any = null;
      try {
        parsedResponse = errorText ? JSON.parse(errorText) : null;
      } catch {
        parsedResponse = errorText || null;
      }

      const detail = parsedResponse?.detail;
      const detailMessage = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((item: any) => item?.msg || item?.message).filter(Boolean).join(' | ')
          : parsedResponse?.message;

      const message = detailMessage || `HTTP ${r.status}: ${r.statusText}`;

      throw new ApiError(r.status, r.statusText, parsedResponse ?? errorText, message);
    }

    const res: ProjectUserWithRole = await r.json();
    logger.log('addUserToProject: success', res);

    return res;
  } catch(e: Error | any) {
    throw e;
  }
};

/**
 * Update a user in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to update the user in.
 * @param {string} userId - The ID of the user to update.
 * @param {any} userData - The data to update the user with.
 * @returns {Promise<ProjectUserWithRole>} A promise that resolves to the updated user.
 * @throws Will throw an error if the request fails.
 */
export const updateUserInProject = async (_token: string, id: string, userId: string, userData: any): Promise<ProjectUserWithRole | null> => {
  try {
    // Build request data according to API specification
    // All fields are optional except id, project_id, user_id
    const requestData: any = {
      id: userData.id, // Required: record ID
      project_id: id,
      user_id: userId
    };

    // Add optional fields only if they exist
    if (userData.project_role_id) {
      requestData.project_role_id = userData.project_role_id;
    }
    
    if (userData.escalation_level_id) {
      requestData.escalation_level_id = userData.escalation_level_id;
    }
    
    if (userData.first_name) {
      requestData.first_name = userData.first_name;
    }
    
    if (userData.last_name) {
      requestData.last_name = userData.last_name;
    }
    
    if (userData.email) {
      requestData.email = userData.email;
    }
    
    if (userData.position) {
      requestData.position = userData.position;
    }
    
    if (userData.phone) {
      requestData.phone = userData.phone;
    }
    
    if (userData.department) {
      requestData.department = userData.department;
    }
    
    if (userData.status) {
      requestData.status = userData.status;
    }
    
    if (userData.skills) {
      requestData.skills = userData.skills;
    }

    const r = await authenticatedFetch(`${API_URL}/projects/${id}/users/${userId}`,{
      method: 'PUT',
      body: JSON.stringify(requestData)
    });
    
    const text = await r.text();
    const res = text ? JSON.parse(text) : null;
    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Remove a user from a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to remove the user from.
 * @param {string} userId - The ID of the user to remove from the project.
 * @returns {Promise<ProjectUserWithRole>} A promise that resolves to the removed user.
 * @throws Will throw an error if the request fails.
 */
export const deleteUserFromProject = async (_token: string, id: string, userId: string): Promise<ProjectUserWithRole> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/users/${userId}`,{
      method: 'DELETE'
    })
    const res: ProjectUserWithRole = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve all items of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to retrieve the items from.
 * @returns {Promise<ProjectItemInterface[]>} A promise that resolves to the list of items.
 * @throws Will throw an error if the request fails.
 */
/**
 * Fetch one page of a project's items. Pass `useCache` to reuse a recent identical response.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} id - the project ID
 * @param {ItemListQuery} query - paging, sort and filter options
 * @param {{useCache?: boolean}} options - set `useCache` to read from and write to the in-memory list cache
 * @returns {Promise<Paginated<ProjectItemInterface>>} the requested page
 */
export const getProjectItems = async (_token: string, id: string, query: ItemListQuery = {}, options: { useCache?: boolean } = {}): Promise<Paginated<ProjectItemInterface>> => {
  const cacheKey = itemListCacheKey('items', id, query);
  if (options.useCache) {
    const cached = getCachedItemList<Paginated<ProjectItemInterface>>(cacheKey);
    if (cached) return cached;
  }
  const data = await fetchProjectItemsPage(_token, id, query);
  if (options.useCache) setCachedItemList(cacheKey, data);
  return data;
};

const fetchProjectItemsPage = async (_token: string, id: string, query: ItemListQuery): Promise<Paginated<ProjectItemInterface>> => {
  try {
    const params = new URLSearchParams();
    appendPageParams(params, query, LIST_DEFAULT_SIZE);
    appendFlag(params, 'archived', query.archived);
    appendTextFilter(params, 'name', query.name);
    appendTextFilter(params, 'description', query.description);
    appendListFilter(params, 'phase_id', query.phaseIds);
    appendListFilter(params, 'owner_id', query.ownerIds);
    appendListFilter(params, 'assigned_to', query.assignedToIds);
    appendFlag(params, 'owner_is_null', query.ownerIsNull);
    appendFlag(params, 'assigned_to_is_null', query.assignedToIsNull);
    appendListFilter(params, 'status_id', query.statusIds);
    appendListFilter(params, 'priority_id', query.priorityIds);
    appendListFilter(params, 'status', query.statusValues);
    appendListFilter(params, 'priority', query.priorityValues);
    appendTextFilter(params, 'created_at_from', query.createdAtFrom);
    appendTextFilter(params, 'created_at_to', query.createdAtTo);
    appendTextFilter(params, 'start_date_from', query.startDateFrom);
    appendTextFilter(params, 'start_date_to', query.startDateTo);
    appendTextFilter(params, 'due_date_from', query.dueDateFrom);
    appendTextFilter(params, 'due_date_to', query.dueDateTo);
    appendTextFilter(params, 'q', query.search);
    appendTextFilter(params, 'risk', query.risk);
    appendListFilter(params, 'room_id', query.roomIds);
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/items?${params}`);
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    return await r.json();
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to fetch project items');
  }
};

/**
 * Create a new item in a project.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} id - The ID of the project to create the item in.
 * @param {ProjectItemInterface} data - The data to create the item with.
 * @returns {Promise<ProjectItemInterface>} A promise that resolves to the created item.
 * @throws Will throw an error if the request fails.
 */
export const createProjectItem = async (_token: string, id: string, data: ProjectItemInterface): Promise<boolean> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/projects/${id}/items`,{
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${response.statusText}. Server response: ${errorText}`);
    }

    return true;
  } catch(e: Error | any) {
    throw new Error(e.message || e);
  }
};

/**
 * Update an item in a project.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} id - The ID of the project to update the item in.
 * @param {string} itemId - The ID of the item to update.
 * @param {ProjectItemInterface} data - The data to update the item with.
 * @returns {Promise<ProjectItemInterface>} A promise that resolves to the updated item.
 * @throws Will throw an error if the request fails.
 */
export const updateProjectItem = async (_token: string, id: string, itemId: string, data: ProjectItemInterface): Promise<boolean> => {
  try {
    console.log('🔧 updateProjectItem called with:', {
      projectId: id,
      itemId: itemId
    });

    const response = await authenticatedFetch(`${API_URL}/projects/${id}/items/${itemId}`,{
      method: 'PUT',
      body: JSON.stringify(data)
    });

    console.log('📡 updateProjectItem response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ updateProjectItem error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}. Server response: ${errorText}`);
    }

    console.log('✅ updateProjectItem successful');
    return true;
  } catch(e: Error | any) {
    console.error('❌ updateProjectItem exception:', e);
    throw e;
  }
};

/**
 * Delete an item from a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to delete the item from.
 * @param {string} itemId - The ID of the item to delete.
 * @returns {Promise<ProjectItemInterface>} A promise that resolves to the deleted item.
 * @throws Will throw an error if the request fails.
 */
export const deleteProjectItem = async (_token: string, id: string, itemId: string): Promise<ProjectItemInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/items/${itemId}`,{
      method: 'DELETE'
    })
    const res:ProjectItemInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve all sites of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to retrieve the sites from.
 * @returns {Promise<ProjectSiteInterface[]>} A promise that resolves to the list of sites.
 * @throws Will throw an error if the request fails.
 */
export const getProjectSites = async (_token: string, id: string, options?: { archived?: boolean }): Promise<ProjectSiteInterface[]> => {
  try {
    const url = new URL(`${API_URL}/projects/${id}/sites`);
    url.searchParams.set('size', '1000');
    if (options?.archived) {
      url.searchParams.set('archived', 'true');
    }
    const r = await authenticatedFetch(url.toString());
    const data:ProjectSiteInterface[] = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create a new site in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to create the site in.
 * @param {ProjectSiteInterface} data - The data to create the site with.
 * @returns {Promise<ProjectSiteInterface>} A promise that resolves to the created site.
 * @throws Will throw an error if the request fails.
 */
export const createProjectSite = async (_token: string, id: string, data: ProjectSiteItemInterface): Promise<ProjectSiteItemInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/sites`,{
      method: 'POST',
      body: JSON.stringify(data)
    })
    const res:ProjectSiteItemInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update a site in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to update the site in.
 * @param {string} siteId - The ID of the site to update.
 * @param {ProjectSiteItemInterface} data - The data to update the site with.
 * @returns {Promise<ProjectSiteItemInterface>} A promise that resolves to the updated site.
 * @throws Will throw an error if the request fails.
 */
export const updateProjectSite = async (_token: string, id: string, siteId: string, data: ProjectSiteItemInterface): Promise<ProjectSiteItemInterface> => {
  try {
    // Ensure project_id is included in the request body
    const requestData = {
      ...data,
      project_id: id
    };
    
    const r = await authenticatedFetch(`${API_URL}/projects/${id}/sites/${siteId}`,{
      method: 'PUT',
      body: JSON.stringify(requestData)
    })
    
    if (!r.ok) {
      // Try to get response body for better error information
      let responseBody = '';
      try {
        responseBody = await r.text();
      } catch (e) {
        responseBody = 'Could not read response body';
      }
      
      throw new Error(`HTTP ${r.status}: ${r.statusText}. Response: ${responseBody}`);
    }
    
    // Check if response has content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
    const res:ProjectSiteItemInterface = await r.json();
      return res;
    }

    // For 204 No Content or other successful responses without JSON body
    // Return the input data as the "updated" site (optimistic update)
    return data;
  } catch(e: Error | any) {
    throw new Error(e.message || e);
  }
};

/**
 * Update a site directly by site ID.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} siteId - The ID of the site to update.
 * @param {any} data - The data to update the site with.
 * @returns {Promise<any>} A promise that resolves when the site is updated.
 * @throws Will throw an error if the request fails.
 */
export const updateSite = async (_token: string, siteId: string, data: any): Promise<any> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!r.ok) {
      // Try to get response body for better error information
      let responseBody = '';
      try {
        responseBody = await r.text();
      } catch (e) {
        responseBody = 'Could not read response body';
      }
      
      throw new Error(`HTTP ${r.status}: ${r.statusText}. Response: ${responseBody}`);
    }

    // Check if response has content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await r.json();
    }
    
    return true; // Success for 204 No Content
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to update site');
  }
};

// Keep the old place functions for backwards compatibility
/**
 * Get place information for a project item.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} projectId - The ID of the project.
 * @param {string} itemId - The ID of the item.
 * @returns {Promise<any>} A promise that resolves to the place information.
 * @throws Will throw an error if the request fails.
 */
export const getProjectItemPlace = async (_token: string, projectId: string, itemId: string): Promise<any> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/place`);
    const data = await r.json();
    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create or update place information for a project item.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} projectId - The ID of the project.
 * @param {string} itemId - The ID of the item.
 * @param {any} data - The place data to save.
 * @returns {Promise<any>} A promise that resolves to the saved place information.
 * @throws Will throw an error if the request fails.
 */
export const saveProjectItemPlace = async (_token: string, projectId: string, itemId: string, data: any): Promise<any> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/place`,{
      method: 'POST',
      body: JSON.stringify(data)
    })
    const res = await r.json();
    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Add asset to project item
 */
export const addAssetToProjectItem = async (
  _token: string, 
  projectId: string, 
  itemId: string, 
  assetId: string
): Promise<any> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/assets`, {
      method: 'POST',
      body: JSON.stringify({ asset_id: assetId })
    });

    if (!response.ok) {
      throw new Error(await parseApiErrorMessage(response, 'Failed to add asset to project item'));
    }

    return await response.json();
  } catch (error) {
    logger.error('Error adding asset to project item:', error);
    throw error;
  }
};

/**
 * Remove asset from project item
 */
export const removeAssetFromProjectItem = async (
  _token: string, 
  projectId: string, 
  itemId: string, 
  assetId: string
): Promise<any> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/assets/${assetId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error(`Failed to remove asset from project item: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error removing asset from project item:', error);
    throw error;
  }
};

/**
 * Get assets for project item
 */
export const getAssetsForProjectItem = async (
  _token: string, 
  projectId: string, 
  itemId: string
): Promise<any[]> => {
  try {
    console.log('API: Fetching assets for item:', itemId);
    const response = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/assets`);

    console.log('API: Response status:', response.status);
    
    if (!response.ok) {
      console.warn(`Assets API returned ${response.status} for item ${itemId}`);
      return [];
    }

    const data = await response.json();
    console.log('API: Raw response data:', data);
    
    const results = Array.isArray(data) ? data : data.results || [];
    console.log('API: Processed results:', results);
    
    return results;
  } catch (error) {
    console.error('Error getting assets for project item:', error);
    return [];
  }
};

// The timezone list is static (~440 IANA zones), so it is fetched once per session.
let timezonesCache: string[] | null = null;

/**
 * Get the list of IANA timezones supported by the backend.
 * Available to any authenticated user - no role restriction.
 * The result is cached for the lifetime of the page.
 * @returns {Promise<string[]>} the list of IANA timezone identifiers
 * @throws if the request fails
 */
export const getTimezones = async (): Promise<string[]> => {
  if (timezonesCache) return timezonesCache;

  const r = await authenticatedFetch(`${API_URL}/projects/timezones`);

  if (!r.ok) {
    throw new Error(await parseApiErrorMessage(r, 'Failed to load the list of timezones'));
  }

  const data = await r.json();
  // The endpoint is documented as `{ timezones: [...] }` but is untyped in the
  // OpenAPI schema, so accept a bare array too.
  const timezones: string[] = Array.isArray(data) ? data : data?.timezones || [];

  timezonesCache = timezones;
  return timezones;
};

/**
 * Set the project timezone from an IANA timezone string.
 * Requires the agency-admin or agency-project-manager role (enforced by the API).
 * @param {string} projectId - the ID of the project to update
 * @param {string} timezone - IANA timezone identifier, e.g. "Europe/Warsaw"
 * @returns {Promise<{ id: string; timezone: string }>} the project ID and its new timezone
 * @throws if the request fails
 */
export const updateProjectTimezone = async (
  projectId: string,
  timezone: string
): Promise<{ id: string; timezone: string }> => {
  const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/timezone`, {
    method: 'PATCH',
    body: JSON.stringify({ timezone })
  });

  if (!r.ok) {
    if (r.status === 403) {
      throw new Error('You do not have permission to change the project timezone');
    }
    throw new Error(await parseApiErrorMessage(r, 'Failed to update the project timezone'));
  }

  return await r.json();
};

/**
 * Set the project timezone by resolving geographic coordinates to an IANA timezone.
 * The API resolves and saves in a single call - there is no dry-run variant.
 * Requires the agency-admin or agency-project-manager role (enforced by the API).
 * @param {string} projectId - the ID of the project to update
 * @param {number} latitude - latitude between -90 and 90
 * @param {number} longitude - longitude between -180 and 180
 * @returns {Promise<{ id: string; timezone: string }>} the project ID and the resolved timezone
 * @throws if the coordinates cannot be resolved or the request fails
 */
export const updateProjectTimezoneFromGeolocation = async (
  projectId: string,
  latitude: number,
  longitude: number
): Promise<{ id: string; timezone: string }> => {
  const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/timezone/geolocation`, {
    method: 'PATCH',
    body: JSON.stringify({ latitude, longitude })
  });

  if (!r.ok) {
    if (r.status === 400) {
      throw new Error('Could not determine a timezone for those coordinates. Please pick one from the list.');
    }
    if (r.status === 403) {
      throw new Error('You do not have permission to change the project timezone');
    }
    throw new Error(await parseApiErrorMessage(r, 'Failed to detect the project timezone'));
  }

  return await r.json();
};

/**
 * Get items for project asset
 */
export const getItemsForProjectAsset = async (
  _token: string, 
  projectId: string, 
  assetId: string
): Promise<any[]> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/projects/${projectId}/assets/${assetId}/items`);

    if (!response.ok) {
      throw new Error(`Failed to get items for project asset: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error('Error getting items for project asset:', error);
    return [];
  }
};
